import dayjs from 'dayjs';
import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** The kind of record comments/logs attach to (e.g. "Task"). */
export type EntityType = 'Task' | 'Engagement' | string;

/** A comment on an entity (fields read defensively via the accessors below). */
export interface Comment {
  id: number;
  entity_type?: string;
  entity_id?: number;
  user?: number | { id?: number; full_name?: string; name?: string } | null;
  user_name?: string | null;
  comment?: string | null;
  created_at?: string | null;
}

/** A change-log entry for an entity. */
export interface ActivityLog {
  id: number;
  entity_type?: string;
  entity_id?: number;
  user?: number | { id?: number; full_name?: string; name?: string } | null;
  user_name?: string | null;
  action?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  created_at?: string | null;
}

export interface CreateCommentPayload {
  entity_type: EntityType;
  entity_id: number;
  user: number;
  comment: string;
}

export interface CreateLogPayload {
  entity_type: EntityType;
  entity_id: number;
  user: number;
  action: string;
  old_value?: string;
  new_value?: string;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/* ------------------------------------------------------------------ *
 * Defensive accessors.
 * ------------------------------------------------------------------ */
const personName = (
  user: number | { id?: number; full_name?: string; name?: string } | null | undefined,
  fallbackName?: string | null,
): string => {
  if (fallbackName) return fallbackName;
  if (user && typeof user === 'object') return user.full_name || user.name || '';
  return '';
};

export const commentUserName = (c: Comment) => personName(c.user, c.user_name) || 'User';
export const commentText = (c: Comment) => c.comment || '';
export const commentWhen = (c: Comment) => (c.created_at ? dayjs(c.created_at).format('MMM D • h:mm A') : '');

export const logUserName = (l: ActivityLog) => personName(l.user, l.user_name);
export const logWhen = (l: ActivityLog) =>
  l.created_at ? dayjs(l.created_at).format('MMM D • h:mm A') : '';
/** A human title for a log action, e.g. "status_changed" → "Status Changed". */
export const logTitle = (l: ActivityLog) =>
  (l.action || 'Updated').replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
/** A subtitle describing the change (old → new) plus who did it. */
export const logDetail = (l: ActivityLog): string => {
  const who = logUserName(l);
  if (l.old_value && l.new_value) {
    const by = who ? ` by ${who}` : '';
    return `${l.old_value} → ${l.new_value}${by}`;
  }
  return who ? `By ${who}` : '';
};

export const activityApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getComments: build.query<Comment[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityComment}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<Comment>) => asList(r),
      providesTags: ['Comments'],
    }),
    createComment: build.mutation<Comment, CreateCommentPayload>({
      query: (body) => ({ url: `${ApiEndPoints.activityComment}/`, method: 'POST', body }),
      invalidatesTags: ['Comments'],
    }),
    getActivityLog: build.query<ActivityLog[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityLog}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<ActivityLog>) => asList(r),
      providesTags: ['ActivityLog'],
    }),
    createActivityLog: build.mutation<ActivityLog, CreateLogPayload>({
      query: (body) => ({ url: `${ApiEndPoints.activityLog}/`, method: 'POST', body }),
      invalidatesTags: ['ActivityLog'],
    }),
  }),
});

export const {
  useGetCommentsQuery,
  useCreateCommentMutation,
  useGetActivityLogQuery,
  useCreateActivityLogMutation,
} = activityApi;
